import { ScreenSize } from '@sunrise/types'

export type AmsSearchRequest = {
    [AmsRequestKeys.AccidentFree]: boolean | null
    [AmsRequestKeys.Advertiser]: string[]
    [AmsRequestKeys.BodyColor]: string[]
    [AmsRequestKeys.BodyType]: string[]
    [AmsRequestKeys.CustomerType]: string | null
    [AmsRequestKeys.Debug]: boolean
    [AmsRequestKeys.DoorsMax]: number | null
    [AmsRequestKeys.DoorsMin]: number | null
    [AmsRequestKeys.EmissionClass]: string[]
    [AmsRequestKeys.EmissionSticker]: string[]
    [AmsRequestKeys.Equipment]: string[]
    [AmsRequestKeys.FirstRegistrationDateMax]: number | null
    [AmsRequestKeys.FirstRegistrationDateMin]: number | null
    [AmsRequestKeys.ForceValidity]: string | null
    [AmsRequestKeys.Fuel]: string[]
    [AmsRequestKeys.HpMax]: number | null
    [AmsRequestKeys.HpMin]: number | null
    [AmsRequestKeys.KwMax]: number | null
    [AmsRequestKeys.KwMin]: number | null
    [AmsRequestKeys.Latitude]: number | null
    [AmsRequestKeys.Location]: string
    [AmsRequestKeys.LocationName]: string
    [AmsRequestKeys.Longitude]: number | null
    [AmsRequestKeys.MakeId]: string[]
    [AmsRequestKeys.MileageMax]: number | null
    [AmsRequestKeys.MileageMin]: number | null
    [AmsRequestKeys.ModelId]: string[]
    [AmsRequestKeys.OfferType]: string[]
    [AmsRequestKeys.OnlinePurchaseOnly]: boolean
    [AmsRequestKeys.Page]: number
    [AmsRequestKeys.PageSize]: number
    [AmsRequestKeys.PriceMax]: number | null
    [AmsRequestKeys.PriceMin]: number | null
    [AmsRequestKeys.Radius]: number
    [AmsRequestKeys.SeatsMax]: number | null
    [AmsRequestKeys.SeatsMin]: number | null
    [AmsRequestKeys.Sort]: SortKeys
    [AmsRequestKeys.TransmissionType]: string[]
    [AmsRequestKeys.UseOneItemsListInResponse]: boolean
    [AmsRequestKeys.WithImagesOnly]: boolean | null
    [AmsRequestKeys.Zip]: string | null
    [AmsRequestKeys.DeliveryOption]: DeliveryOption
}

export type AmsSearchParam = {
    [Property in keyof AmsSearchRequest]?: AmsSearchRequest[Property] | null
}

export type AmsSearchByUrlRequest = Pick<AmsSearchRequest, AmsRequestKeys.Latitude | AmsRequestKeys.Longitude> & {
    urlName: string
    useOneItemsListInResponse: boolean
}

export type AmsSearchByUrlParams = Pick<
    AmsSearchRequest,
    AmsRequestKeys.Location | AmsRequestKeys.LocationName | AmsRequestKeys.Latitude | AmsRequestKeys.Longitude
> & { [AmsRequestKeys.Sort]?: AmsSearchRequest[AmsRequestKeys.Sort] | null }

export type AmsSearchByUrlRawResponse = RawSearchResponse & {
    [AmsRequestKeys.Location]: null
    [AmsRequestKeys.LocationName]: null
}

export enum AmsRequestKeys {
    AccidentFree = 'accidentFree',
    Advertiser = 'advertiser',
    BodyColor = 'bodyColor',
    BodyType = 'bodyType',
    CustomerType = 'customerType',
    Debug = 'debug',
    DoorsMax = 'doorsMax',
    DoorsMin = 'doorsMin',
    EmissionClass = 'emissionClass',
    EmissionSticker = 'emissionSticker',
    Equipment = 'equipment',
    FirstRegistrationDateMax = 'firstRegistrationDateMax',
    FirstRegistrationDateMin = 'firstRegistrationDateMin',
    ForceValidity = 'forceValidity',
    Fuel = 'fuel',
    HpMax = 'hpMax',
    HpMin = 'hpMin',
    KwMax = 'kwMax',
    KwMin = 'kwMin',
    Latitude = 'latitude',
    Location = 'location',
    LocationName = 'locationName',
    Longitude = 'longitude',
    MakeId = 'makeId',
    MileageMax = 'mileageMax',
    MileageMin = 'mileageMin',
    ModelId = 'modelId',
    OfferType = 'offerType',
    OnlinePurchaseOnly = 'onlinePurchaseOnly',
    Page = 'page',
    PageSize = 'pageSize',
    PriceMax = 'priceMax',
    PriceMin = 'priceMin',
    Radius = 'radius',
    SeatsMax = 'seatsMax',
    SeatsMin = 'seatsMin',
    Sort = 'sort',
    TransmissionType = 'transmissionType',
    UseOneItemsListInResponse = 'useOneItemsListInResponse',
    WithImagesOnly = 'withImagesOnly',
    Zip = 'zip',
    DeliveryOption = 'deliveryOption',
}

export enum SortKeys {
    OurRecommendationADesc = 'our_recommendation_a_desc',
    DistanceAscAndCpcDesc = 'distance_asc_and_cpc_desc',
    LatestOffersAndCpcDesc = 'latest_offers_and_cpc_desc',
    FocusOnPriceADesc = 'focus_on_price_a_desc',
    PriceAsc = 'price_asc',
}

export enum DeliveryOption {
    All = 'ALL',
    OnSite = 'ONSITE',
    OnLine = 'ONLINE',
}

export type RawSearchResponse = Omit<AmsSearchResponse, 'items'> & { items: Omit<AmsItem, 'resizedImages'>[] }

export interface AmsSearchResponse {
    request: AmsSearchRequest
    sortCriteria: string[]
    items: AmsItem[]
    pageSize: number
    page: number
    totalHits: number
    filters: Filter[]
    selectedValues: MakeAndModelName
}

export interface MakeAndModelName {
    makeName: string
    modelName: string
}

export interface Filter {
    key: FilterKeys
    values: FilterValue[]
}

export enum FilterKeys {
    OfferType = 'offer_type_key',
    BodyType = 'body_type_key',
    Fuel = 'primary_fuel_type_key',
    BodyColor = 'body_color_key',
    Equipment = 'equipment_key',
    TransmissionType = 'transmission_type_key',
    EmissionClass = 'emission_class_key',
}

export interface FilterValue {
    key: string
    label: string
    count: number
    selected: boolean
}

export enum OfferType {
    Demonstration = 'DEMONSTRATION',
    Oldtimer = 'OLDTIMER',
    Jahreswagen = 'JAHRESWAGEN',
    UsedCar = 'USED_CAR',
    NewCar = 'NEW_CAR',
    SingleDay = 'SINGLE_DAY',
}

export interface AmsItem {
    externalId: string
    advertiser: Advertiser
    advertiserKey: AdvertiserKey
    advertiserLogo: string
    url: string
    title: string
    make: string
    model: string
    makeId: string
    modelId: string
    mileage: number
    numberOfPreviousOwners: number
    fuelConsumptionCombined: string
    co2emissionInGramPerKm: number
    powerInHp: number
    powerInKw: number
    initialRegistrationDate: number
    initialRegistrationDateMonth?: number
    usageState: UsageState
    transmissionType: string
    fuelType: FuelType
    price: number
    priceFormatted: string
    city?: string
    zip?: number
    images: string[]
    resizedImages: ResizedItemImages[]
    bodyColor?: string
    bodyTypeKey: BodyTypeKey
    accidentFree: boolean
    badges: Badge[]
    recommended: boolean
    position: number
    onlinePurchase: boolean
    additionalItem: boolean
    distance: number
    imageCount: number
    deliveryOption: DeliveryOption
    priceIndicator: PriceIndicator
}

export interface PriceIndicator {
    priceClass: PriceClass
    class1: number
    class2: number
    class3: number
    class4: number
    class5: number
    class6: number
    comparedVehicleCount: number
}

export type ResizedItemImages = {
    [key in ScreenSize]: string | null
}

export enum FuelType {
    Gasoline = 'Benzin',
    NaturalGasCNG = 'Erdgas (CNG)',
    Electro = 'Elektro',
}

export enum BodyTypeKey {
    Compact = 'COMPACT',
    Offroad = 'OFFROAD',
    Sedan = 'SEDAN',
}

export enum Badge {
    AnnualCar = 'Jahreswagen',
    YoungUsed = 'junger Gebrauchter',
    LowConsumption = 'wenig gelaufen',
    NewCar = 'Neuwagen',
    Youngtimer = 'Youngtimer',
}

export enum Advertiser {
    InstaMotion = 'InstaMotion',
}

export enum AdvertiserKey {
    As24 = 'AS24',
    PkwDe = 'PKWDE',
    Instamotion = 'INSTAMOTION',
    AutoDe = 'AUTODE',
    MobileDe = 'MOBILEDE',
    Smyle = 'SMYLE',
}

export enum UsageState {
    Used = 'Gebraucht',
    New = 'Neu',
}

export enum PriceClass {
    NotSpecified = 0,
    VeryGoodPrice = 1,
    GoodPrice = 2,
    FairPrice = 3,
    IncreasedPrice = 4,
    HighPrice = 5,
}
